import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CartLinesFromDB } from "../../services/PortfolioModelWalletService";
import {
  CommandBar,
  IButtonProps,
  ICommandBarItemProps,
  Spinner,
} from "@fluentui/react";
import { BACKTESTING_DESCRIPTION, DESCRIPTIONS, TTabType } from "./Texts/texts";
import Backtesting, { TRatioAndPeriod } from "./Tabs/Backtesting/Backtesting";
import OmniaServices, {
  RatioKeys,
  TDataDiversificacion,
  TOmniaSimulationResponse,
  TOptimizarFondosPayload,
  TRatiosKeyCompletos,
} from "../../services/OmniaService";
import DiversificacionSimulacion, {
  TDiversificacionSimulacionData,
} from "./Tabs/Diversificacion/DiversificacionSimulacion";
import { DataToDivesificationCall } from "./Utils/DataToDiversificationCall";
import {
  FormatDestinyFundsData,
  FormatFundsData,
} from "./Utils/FormatFundsData";
import { FormatToSimulation } from "./Utils/FormatToSimulation";
import { TSelectionState } from "./Tables/TableDestino";
import { TRatiosCarterasData } from "../Portfolios/File/types/CarteraRatioTypes";
import { dataFromSimulationToTable } from "./Utils/FormatDataFromSimulation";
import BackButton from "../../components/buttons/BackButton";
import { IInitialRegisters } from "../Portfolios/Summary/types/PortfolioTypes";

const BASE_CLASS = "simulation";

export type TTableData = TSearchData & {
  originalWeight: number;
  destinyWeight: number;
  posicion_fondo_original: number;
  total_fondos_categoria: number;
  ratiosCompletos: {
    fondo_original: TRatiosKeyCompletos;
    fondo_nuevo: TRatiosKeyCompletos;
  };
};

export type TSearchData = {
  originalIsin: string;
  originalName: string;
  destinyIsin: string;
  destinyName: string;
};

const Simulation = () => {
  const location = useLocation();

  const localTheme = window.localStorage.getItem("theme") || "light";
  const [funds] = useState<CartLinesFromDB[]>(
    Array.isArray((location.state as any)?.funds)
      ? (location.state as any).funds.filter(
          (fund: IInitialRegisters) => fund.instrumentType === 21,
        )
      : [],
  );
  const [description, setDescription] = useState<string>(
    BACKTESTING_DESCRIPTION,
  );
  const [cargaDiversificacion, setCargaDiversificacion] =
    useState<boolean>(false);
  const [disableDiversificacionTab, setDisableDiversificacionTab] =
    useState<boolean>(true);
  const [ratioPeriodFlashBackError, setRatioPeriodFlashBackError] =
    useState<boolean>(true);

  const [diversificacionData, setDiversificacionData] =
    useState<TDiversificacionSimulacionData>();
  const [simulationData, setSimulationData] =
    useState<TOmniaSimulationResponse>();
  const [walletsData, setWalletsData] = useState<TRatiosCarterasData[]>([]);

  const [nuevosFunds, setNuevosFunds] = useState<TTableData[]>([]);
  const [payload, setPayload] = useState<TOptimizarFondosPayload>({
    periodo: "1y",
    ratio: "linear_return",
    semanas_atras: 0,
  } as TOptimizarFondosPayload);
  const [ratioAndPeriod, setRatioAndPeriod] = useState<TRatioAndPeriod>(
    {} as TRatioAndPeriod,
  );

  const _overflowItems: ICommandBarItemProps[] = [];
  const overflowProps: IButtonProps = { ariaLabel: "Más acciones" };

  const [activeTab, setActiveTab] = useState<string>("backtesting");

  useEffect(() => {
    const isins: string[] = funds.map((fund) => fund.isin);
    setPayload((prev) => ({
      ...prev,
      isins: isins,
    }));
  }, [funds]);

  const BAR_TABS: ICommandBarItemProps[] = [
    {
      text: "Backtesting",
      key: "backtesting",
      onClick: () => setActiveTab("backtesting"),
      className: activeTab === "backtesting" ? "is-selected" : "",
    },
    {
      disabled: disableDiversificacionTab,
      text: "Diversificación",
      key: "diversificacion",
      onClick: () => setActiveTab("diversificacion"),
      className: activeTab === "diversificacion" ? "is-selected" : "",
    },
  ];

  const prepareDiversification = async (fondosData: TSelectionState) => {
    const originalCallData = DataToDivesificationCall({
      fundsData: fondosData,
      isOrigen: true,
    });
    const nuevoCallData = DataToDivesificationCall({
      fundsData: fondosData,
      isOrigen: false,
    });

    const [originalDiversificacionData, nuevoDiversificacionData]: [
      TDataDiversificacion,
      TDataDiversificacion,
    ] = await Promise.all([
      OmniaServices.DiversificacionCartera(originalCallData),
      OmniaServices.DiversificacionCartera(nuevoCallData),
    ]);

    setDiversificacionData({} as TDiversificacionSimulacionData);
    setDiversificacionData({
      original: originalDiversificacionData,
      nuevo: nuevoDiversificacionData,
    });
    setCargaDiversificacion(false);
    setDisableDiversificacionTab(false);
  };

  useEffect(() => {
    setDescription(DESCRIPTIONS[activeTab as TTabType] || "");
  }, [activeTab]);

  const searchBetterFunds = () => {
    OmniaServices.OptimizarFondos(payload).then((resultado) => {
      const formatDestinyFundsdataResult = FormatDestinyFundsData(
        resultado.resultados,
        FormatFundsData(funds),
        payload,
      );
      setNuevosFunds(formatDestinyFundsdataResult);
      setRatioAndPeriod({
        ratio: payload.ratio as RatioKeys,
        period: payload.periodo,
      });
    });
  };

  const updatePayload = (inputData: string, option: string) => {
    switch (option) {
      case "ratio":
        setPayload((prev) => ({ ...prev, ratio: inputData }));
        break;

      case "periodo":
        setPayload((prev) => ({ ...prev, periodo: inputData }));
        break;

      case "semanasAtras":
        setPayload((prev) => ({ ...prev, semanas_atras: Number(inputData) }));
        break;

      default:
        break;
    }
  };

  useEffect(() => {
    const isError =
      payload.periodo !== null &&
      payload.ratio !== null &&
      payload.semanas_atras >= 0;
    setRatioPeriodFlashBackError(isError);
  }, [payload]);

  const simulationCall = async (selected: TSelectionState) => {
    setCargaDiversificacion(true);
    setDisableDiversificacionTab(true);
    prepareDiversification(selected);

    const payloadSimulation = FormatToSimulation(selected);

    const simulationResponse: TOmniaSimulationResponse =
      await OmniaServices.SimularFondos(payloadSimulation);

    const originalTableData: TRatiosCarterasData = dataFromSimulationToTable({
      ratiosHistoricos: simulationResponse.resultados?.ratios_historicos,
      option: "Original",
    });

    const nuevoTableData: TRatiosCarterasData = dataFromSimulationToTable({
      ratiosHistoricos: simulationResponse.resultados?.ratios_historicos,
      option: "Nuevo",
    });

    setWalletsData([originalTableData, nuevoTableData]);
    setSimulationData(simulationResponse);
  };

  return (
    <div className={BASE_CLASS}>
      <div className={`${BASE_CLASS}__header`}>
        <div className={`${BASE_CLASS}__header__texts`}>
          <BackButton />

          <h2>IronIA Optimizer</h2>
          <div className={`${BASE_CLASS}__commandBar`}>
            <CommandBar
              // @ts-ignore
              items={BAR_TABS}
              overflowItems={_overflowItems}
              overflowButtonProps={overflowProps}
              ariaLabel="Usa las flechas izquierda y derecha para navegar entre las entradas del menú"
            />
            {cargaDiversificacion && (
              <Spinner size={3} style={{ padding: "1em" }} />
            )}
          </div>
          <p className={`${BASE_CLASS}__header__description ${localTheme}`}>
            {description}
          </p>
          {cargaDiversificacion && (
            <div className={`${BASE_CLASS}__loading`}>
              Se están cargando los datos para acceder a la pestaña de
              "Diversificación"
            </div>
          )}
        </div>
      </div>
      <div className={`${BASE_CLASS}__container`}>
        {activeTab.match("backtesting") && (
          <Backtesting
            originalFunds={FormatFundsData(funds)}
            nuevosFunds={nuevosFunds}
            simulationInfo={simulationData}
            tableData={walletsData}
            searchFunc={async () => searchBetterFunds()}
            simulationFunc={simulationCall}
            ratioPeriod={ratioAndPeriod}
            payloadFunc={updatePayload}
            inputsError={ratioPeriodFlashBackError ?? true}
          />
        )}
        {activeTab.match("diversificacion") && (
          <DiversificacionSimulacion
            data={diversificacionData ?? ({} as TDiversificacionSimulacionData)}
          />
        )}
      </div>
    </div>
  );
};

export default Simulation;
